import { Immutable } from "immer";
import React from "react";
import {
    Gallery,
    IGalleryImage,
} from "tsi-common-react/src/components/Gallery/index";

import type { CarouselModule as Value } from "@reactivated";

type Props = Immutable<{
    value: Value;
}>;

type CarouselItem = NonNullable<Value["carousel"]>[number];

/**
 * React port of the sealy ``CarouselModule`` block. The legacy template
 * serialized gallery images to a JSON blob and handed it to the
 * ``PdpGalleryTemplate`` reactivated component, which JSON.parsed it back
 * out. Here we build the ``IGalleryImage[]`` directly from the typed
 * Wagtail renditions and render the shared ``Gallery`` component.
 *
 * The legacy template wrapped the include in ``{% if value.carousel %}``
 * and added no container markup, so this component returns ``null`` when
 * there are no usable images and renders ``<Gallery>`` bare otherwise.
 *
 * ``alternateArrows`` is hardcoded to ``false``: sealy's
 * ``CarouselModule.alternate_arrows = False`` (server/cms/components/pdp.py)
 * OVERRIDES the ``PdpGalleryCarouselMixin`` default of ``True``, and the
 * legacy template passed that value straight through to
 * ``PdpGalleryTemplate`` -> ``Gallery``. (This matches toppers and diverges
 * from pillows, which keeps the mixin default of ``true`` — do NOT "fix"
 * this back to ``true``.)
 */
const buildGalleryImage = (item: CarouselItem): IGalleryImage | null => {
    const image = item.image;
    if (!image) {
        return null;
    }
    const originalURL = image.renditions["original"]?.url ?? image.url;
    const thumbnailURL = image.renditions["width-200"]?.url ?? image.url;
    // Legacy SSR used the rendition's ``.alt``, which equals Wagtail's
    // ``default_alt_text`` (description-or-title). Prefer that for parity.
    const alt = image.default_alt_text || image.title || "";
    return {
        originalURL,
        originalAlt: alt,
        thumbnailURL,
        thumbnailAlt: alt,
        caption: item.caption ?? "",
    };
};

export const CarouselModule = (props: Props): React.JSX.Element | null => {
    const carousel = props.value.carousel ?? [];
    const images = carousel
        .map((item) => buildGalleryImage(item))
        .filter((img): img is IGalleryImage => img !== null);

    if (images.length === 0) {
        return null;
    }

    return <Gallery images={images} alternateArrows={false} />;
};
